import type { ReactNode, DetailedHTMLProps, LabelHTMLAttributes } from 'react';
import styled from '@emotion/styled';
import * as tokens from '@tokens';
import type { CheckboxInputProps } from '@components/Inputs/Checkbox/CheckboxInput';
import type { RadioInputProps } from '@components/Inputs/Radio/RadioInput';

export interface BaseOptionInputProps {
  /**
   * Label content (React nodes and strings allowed).
   */
  labelContent?: string | ReactNode;

  /**
   * Label props (allows passing HTML attributes down to the label).
   */
  labelHtmlProps?: Omit<
    DetailedHTMLProps<LabelHTMLAttributes<HTMLLabelElement>, HTMLLabelElement>,
    'htmlFor' | 'className'
  >;

  /**
   * Helper text to render under the label (this should rarely be used).
   */
  helperText?: string | ReactNode;

  /**
   * Specifies if the input is disabled.
   */
  isDisabled?: boolean;

  /**
   * Styles overrides.
   */
  className?: string;

  /**
   * Input to render - either Checkbox or Radio.
   */
  children:
    | React.ReactElement<CheckboxInputProps>
    | React.ReactElement<RadioInputProps>;
}

const Label = styled('label', {
  shouldForwardProp: (prop) => prop !== 'isDisabled',
})<{ isDisabled: boolean }>`
  display: inline-flex;
  align-items: flex-start;
  cursor: ${({ isDisabled }) => (isDisabled ? 'not-allowed' : 'pointer')};
  color: ${({ isDisabled }) =>
    isDisabled ? tokens.color.text_disabled : tokens.color.text_default};
  ${tokens.text('body2')};
  text-transform: none; // temp solution until the old styles from IWA are removed
`;

const LabelContent = styled.span`
  flex: 1 1 auto;
  user-select: none;
`;

const HelperText = styled.div`
  ${tokens.text('caption')};
  color: ${tokens.color.text_light};
  margin-top: ${tokens.spacer.x0};
`;

/**
 * BaseOptionInput component.
 *
 * **WARNING!** _This component is for internal use in the Design System. If you
 * need to use it for a custom component, please discuss your use case with
 * the Design System squad before proceeding, as they may be able to help
 * you with a better solution._
 * @summary Provides a base component for radio and checkbox input components
 * @see Checkbox
 */
export const BaseOptionInput = ({
  labelContent,
  helperText,
  className,
  isDisabled = false,
  labelHtmlProps = {},
  children,
}: BaseOptionInputProps): JSX.Element => {
  return (
    <Label {...labelHtmlProps} className={className} isDisabled={isDisabled}>
      {children}
      {labelContent && (
        <LabelContent>
          {labelContent}
          {helperText ? <HelperText>{helperText}</HelperText> : null}
        </LabelContent>
      )}
    </Label>
  );
};

BaseOptionInput.displayName = 'BaseOptionInput';
